/*
  Live fundraiser data binding.

  Looks for [data-fundraiser-live] sections, fetches the configured Monobank proxy,
  and replaces static raised / goal / progress / supporters values with real data.
  The endpoint is the Worker galas-tickets at scan.galas-events.com/api/jar.

  Falls back silently to the server-rendered defaults on any failure so the section
  never appears broken in front of users.
*/
(function () {
  function i18n(key, fallback) {
    var dict = window.FundraiserI18n || {};
    return dict[key] || fallback;
  }

  // A Slavic count needs one/few/many, not just singular/plural. Intl picks the
  // right category per locale, so English gets its own two-form behaviour free.
  function donationsWord(count) {
    var category = 'other';
    try {
      category = new Intl.PluralRules(i18n('lang', 'uk')).select(count);
    } catch (e) {
      category = count === 1 ? 'one' : 'many';
    }
    return i18n('donations_' + category, '') || i18n('donations_other', 'донатів');
  }

  'use strict';

  function formatNumber(value, locale) {
    if (value == null || isNaN(value)) return null;
    try {
      return new Intl.NumberFormat(locale || 'uk', { maximumFractionDigits: 0 }).format(value);
    } catch (_) {
      return String(Math.round(value));
    }
  }

  function bindSection(section) {
    var proxyUrl = section.getAttribute('data-fundraiser-proxy');
    if (!proxyUrl) return;

    var currency = section.getAttribute('data-fundraiser-currency') || '';
    var locale = section.getAttribute('data-fundraiser-locale') || 'uk';

    fetch(proxyUrl, { headers: { Accept: 'application/json' } })
      .then(function (res) {
        if (!res.ok) throw new Error('proxy ' + res.status);
        return res.json();
      })
      .then(function (data) {
        if (!data || !data.ok || !data.jar) return;

        var raisedEl = section.querySelector('[data-fundraiser-raised]');
        var goalEl = section.querySelector('[data-fundraiser-goal]');
        var supportersEl = section.querySelector('[data-fundraiser-supporters]');
        var progressEl = section.querySelector('[data-fundraiser-progress]');
        var progressBarEl = section.querySelector('[data-fundraiser-progress-bar]');
        var donateEl = section.querySelector('.fundraiser-card__donate');

        var raised = formatNumber(data.jar.balance, locale);
        if (raisedEl && raised !== null) raisedEl.textContent = currency + raised;

        // A jar with no goal reports null: hide the goal and the bar rather
        // than show «₴0» and an empty track.
        var hasGoal = typeof data.jar.goal === 'number' && data.jar.goal > 0;
        var goalBlock = section.querySelector('[data-fundraiser-goal-block]');
        if (goalBlock) goalBlock.hidden = !hasGoal;
        if (progressEl) progressEl.hidden = !hasGoal;
        if (hasGoal && goalEl) goalEl.textContent = currency + formatNumber(data.jar.goal, locale);

        if (hasGoal && typeof data.jar.progress === 'number') {
          if (progressBarEl) progressBarEl.style.width = data.jar.progress + '%';
          if (progressEl) progressEl.setAttribute('aria-valuenow', String(data.jar.progress));
        }

        if (!hasGoal) bindMilestone(section, data.jar.balance, currency, locale);

        if (supportersEl && data.supporters && typeof data.supporters.transactions === 'number') {
          supportersEl.textContent = String(data.supporters.transactions);
        }

        // Keep the donate button in sync with the jar shown by the proxy, so
        // the donation always lands in the same jar as the displayed stats.
        if (donateEl && data.jar.donateUrl) {
          var current = donateEl.getAttribute('href');
          if (!current || current === '#' || current === '') {
            donateEl.setAttribute('href', data.jar.donateUrl);
          }
          if (donateEl.hasAttribute('data-donate-trigger')) {
            donateEl.setAttribute('data-donate-jar-url', data.jar.donateUrl);
          }
        }

        bindBreakdown(section, data, locale, currency);
      })
      .catch(function (err) {
        if (window && window.console && window.console.warn) {
          window.console.warn('[fundraiser-live] failed:', err.message);
        }
      });
  }

  // No goal set: climb in steps. The target is the next multiple of the step
  // above the balance, and the bar covers only the current step, so passing a
  // threshold starts a fresh bar rather than shrinking the one already filled.
  function bindMilestone(section, balance, currency, locale) {
    var step = Number(section.getAttribute('data-fundraiser-step')) || 0;
    if (step <= 0 || typeof balance !== 'number' || balance < 0) return;

    var done = Math.floor(balance / step);
    var target = (done + 1) * step;
    var percent = Math.round(((balance - done * step) / step) * 100);

    var goalBlock = section.querySelector('[data-fundraiser-goal-block]');
    var goalEl = section.querySelector('[data-fundraiser-goal]');
    var labelEl = goalBlock && goalBlock.querySelector('.fundraiser-card__amount-label');
    var progressEl = section.querySelector('[data-fundraiser-progress]');
    var barEl = section.querySelector('[data-fundraiser-progress-bar]');
    var noteEl = section.querySelector('[data-fundraiser-milestone]');

    if (goalBlock) goalBlock.hidden = false;
    if (labelEl) labelEl.textContent = i18n('milestone_label', 'Поріг {{ n }}').replace('{{ n }}', String(done + 1));
    if (goalEl) goalEl.textContent = currency + formatNumber(target, locale);
    if (progressEl) {
      progressEl.hidden = false;
      progressEl.setAttribute('aria-valuenow', String(percent));
    }
    if (barEl) barEl.style.width = percent + '%';
    if (noteEl) {
      noteEl.textContent = done > 0
        ? i18n('milestone_done', 'Порогів уже взято: {{ n }}').replace('{{ n }}', String(done))
        : i18n('milestone_hint', '');
      noteEl.hidden = !noteEl.textContent;
    }
  }

  // Card donations arrive in zloty and never touch the jar, so they are shown
  // as a chip beside the headline rather than folded into it. The chip opens a
  // receipt that does the NBU conversion in the open, where a reader can see
  // both the rate and its date.
  function bindBreakdown(section, data, locale, currency) {
    var chip = section.querySelector('[data-fundraiser-extra]');
    var modal = section.querySelector('[data-fundraiser-breakdown]');
    if (!chip || !modal) return;

    var d = data.donations;
    if (!d || !d.total) return;

    var plnText = formatNumber(d.total, locale) + ' zł';
    var chipValue = chip.querySelector('[data-fundraiser-extra-value]');
    if (chipValue) chipValue.textContent = '+ ' + plnText;
    chip.hidden = false;

    var set = function (sel, text) {
      var el = modal.querySelector(sel);
      if (el) el.textContent = text;
    };

    set('[data-breakdown-jar]', currency + formatNumber(data.jar.balance, locale));
    set('[data-breakdown-pln]', plnText);
    set('[data-breakdown-count]', d.count ? '· ' + d.count + ' ' + donationsWord(d.count) : '');

    var rateRow = modal.querySelector('[data-breakdown-rate-label]');
    if (d.rate) {
      if (rateRow) {
        rateRow.textContent =
          i18n('rate_nbu', 'За курсом НБУ')
          + (d.rateDate ? ' ' + i18n('rate_on', 'на') + ' ' + d.rateDate : '')
          + ' — ' + d.rate + ' ₴/zł';
      }
      set('[data-breakdown-pln-uah]', currency + formatNumber(d.inUah, locale));
      set('[data-breakdown-total]', currency + formatNumber(d.grandTotalUah, locale));
    } else {
      // No rate: show the parts honestly instead of inventing a total.
      if (rateRow) rateRow.textContent = i18n('rate_unavailable', 'Курс НБУ недоступний');
      set('[data-breakdown-pln-uah]', '—');
      set('[data-breakdown-total]', currency + formatNumber(data.jar.balance, locale) + ' + ' + plnText);
    }

    // The dialog is the modal family's (modal.css). It moves to <body> so no
    // transformed ancestor in the section can trap its `position: fixed`.
    if (modal.parentNode !== document.body) document.body.appendChild(modal);
    var closeBtn = modal.querySelector('.modal__close');
    var lastFocus = null;
    var hideTimer = 0;
    var reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
    var open = function () {
      lastFocus = document.activeElement;
      clearTimeout(hideTimer);
      modal.hidden = false;
      document.body.style.overflow = 'hidden';
      requestAnimationFrame(function () { modal.classList.add('is-open'); });
      if (closeBtn) closeBtn.focus({ preventScroll: true });
    };
    var close = function () {
      if (modal.hidden) return;
      modal.classList.remove('is-open');
      document.body.style.overflow = '';
      hideTimer = setTimeout(function () { modal.hidden = true; }, reduce.matches ? 0 : 320);
      if (lastFocus && lastFocus.focus) lastFocus.focus({ preventScroll: true });
    };

    chip.addEventListener('click', open);
    modal.querySelectorAll('[data-fundraiser-breakdown-close]').forEach(function (el) {
      el.addEventListener('click', close);
    });
    document.addEventListener('keydown', function (e) {
      if (modal.hidden) return;
      if (e.key === 'Escape') close();
      // The close button is the dialog's only control, so Tab stays on it.
      if (e.key === 'Tab') { e.preventDefault(); if (closeBtn) closeBtn.focus(); }
    });
  }

  function init() {
    var sections = document.querySelectorAll('[data-fundraiser-live]');
    if (!sections.length) return;
    sections.forEach(bindSection);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
